import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  LinearProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Dashboard,
  Settings,
  Support,
  Menu as MenuIcon,
  Logout,
  Info,
} from "@mui/icons-material";
import logo from "../Images/BizBridge-Logo-1.png";
import { useDispatch, useSelector } from "react-redux";
import { updateUserMode } from "../redux/slices/userSlice";
import { Link } from "react-router-dom";
import GroupIcon from "@mui/icons-material/Group";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import HubIcon from "@mui/icons-material/Hub";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PageTitle from "../Components/PageTitle";

const drawerWidth = 230;
const sellerSidebarItems = [
  { text: "Dashboard", icon: <Dashboard />, route: "/seller-dashboard" },
  {
    text: "Buyer Category",
    icon: <GroupIcon />,
    route: "/seller-dashboard/buyer-category",
  },
  {
    text: "Product Category",
    icon: <StorefrontIcon />,
    route: "/seller-dashboard/product-category",
  },
  {
    text: "Catalog",
    icon: <LocalMallIcon />,
    route: "/seller-dashboard/catalog",
  },
  {
    text: "Buyer Network",
    icon: <HubIcon />,
    route: "/seller-dashboard/buyer-network",
  },
  {
    text: "Orders",
    icon: <ShoppingCartIcon />,
    route: "/seller-dashboard/orders",
  },
  {
    text: "Pending Invoices",
    icon: <ReceiptLongIcon />,
    route: "/seller-dashboard/pending-invoices",
  }, 

  {
    text: "Seller Billing",
    icon: <ReceiptLongIcon />,
    route: "/seller-dashboard/billing",
  },



  // {
  //   text: "Kiosk Consumer",
  //   icon: <ReceiptLongIcon />,
  //   route: "/seller-dashboard/generate-qr",
  // },

  { text: "Support", icon: <Support />, route: "/seller-dashboard/support" },
];

export default function SellerDashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSeller, setIsSeller] = useState(user?.mode === "seller");
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);

  const profileCompletion = JSON.parse(
    localStorage.getItem("profileCompletion"),
  );

  useEffect(() => {
    setIsSeller(user?.mode === "seller");
  }, [user?.mode]);

  useEffect(() => {
    const isPathSeller = location.pathname
      .toLowerCase()
      .startsWith("/seller-dashboard");
    const isUserSeller = user?.mode === "seller";
    if (isPathSeller && !isUserSeller) {
      navigate("/buyer-dashboard", { replace: true });
    } else if (!isPathSeller && isUserSeller) {
      navigate("/seller-dashboard", { replace: true });
    }
  }, [location.pathname, user?.mode, navigate]);

  const handleProfileIconClick = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleOpenLogoutModal = () => {
    setOpenLogoutModal(true);
    setProfileMenuAnchor(null);
  };

  const handleCloseLogoutModal = () => setOpenLogoutModal(false);

  const handleLogout = () => {
    localStorage.clear();
    setOpenLogoutModal(false);
    window.location.reload("/login");
  };

  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);

  const openViewMenu = (event) => {
    setViewMenuAnchor(event.currentTarget);
  };

  const closeViewMenu = () => {
    setViewMenuAnchor(null);
  };

  // Get color based on completion percentage
  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return "#10b981"; // green
    if (percentage >= 50) return "#f59e0b"; // amber
    return "#ef4444"; // red
  };

  // Get completion status text
  const getCompletionStatus = (percentage) => {
    if (percentage >= 80) return "Complete";
    if (percentage >= 50) return "Moderate";
    return "Low";
  };

  const drawerContents = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Top Logo Section */}
      <Box
        component={Link}
        to={isSeller ? "/seller-dashboard" : "/buyer-dashboard"}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 2,
          cursor: "pointer",
          textDecoration: "none",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={logo}
            sx={{
              height: 55,
              objectFit: "contain",
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Seller View Label */}
      <Box sx={{ px: 2, py: 1.5, flexShrink: 0 }}>
        <Typography
          sx={{
            fontWeight: 600,
            color: "#2e4254",
            fontSize: 15,
            letterSpacing: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          Seller View
        </Typography>

        {/* Profile Completion Section */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography variant="caption" fontWeight={600} color="#64748b">
              Profile Completion
            </Typography>
            <Tooltip
              title={`${profileCompletion?.percentage || 0}% - ${getCompletionStatus(profileCompletion?.percentage || 0)}`}
            >
              <Info
                sx={{ fontSize: 14, color: "#94a3b8", cursor: "pointer" }}
              />
            </Tooltip>
          </Box>

          <Box sx={{ position: "relative" }}>
            <LinearProgress
              variant="determinate"
              value={profileCompletion?.percentage || 0}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: getCompletionColor(
                    profileCompletion?.percentage || 0,
                  ),
                  borderRadius: 3,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#ffffff",
                fontWeight: 700,
                fontSize: "0.7rem",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              {profileCompletion?.percentage || 0}%
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 0.5,
            }}
          >
            <Chip
              label={getCompletionStatus(profileCompletion?.percentage || 0)}
              size="small"
              sx={{
                backgroundColor:
                  getCompletionColor(profileCompletion?.percentage || 0) + "20",
                color: getCompletionColor(profileCompletion?.percentage || 0),
                fontWeight: 600,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
            <Typography variant="caption" color="#94a3b8">
              {profileCompletion?.isProfileComplete
                ? "✓ Complete"
                : "Complete profile"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ flexShrink: 0 }} />

      {/* Menu Items List - Scrollable Section */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <List sx={{ py: 0 }}>
          {sellerSidebarItems.map(({ text, icon, route }) => (
            <ListItem
              button
              key={text}
              selected={location.pathname === route}
              sx={{
                py: 1,
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                color: "#2e4254",
                background: location.pathname === route ? "#dae4f2" : "none",
                cursor: "pointer",
                transition: "all 0.2s ease",

                "&:hover": {
                  background: "#0b4064",
                  color: "#fff",
                  "& .MuiListItemIcon-root": {
                    color: "#fff",
                  },
                },

                fontWeight: location.pathname === route ? 700 : 600,
                maxWidth: "100%",
                overflowX: "hidden",
              }}
              onClick={() => {
                navigate(route, { replace: true });
                if (isMobile) setMobileOpen(false);
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === route ? "#0b4064" : "#4b4b4b",
                  minWidth: 0,
                  pr: 1,
                  transition: "color 0.2s ease",
                }}
              >
                {icon}
              </ListItemIcon>

              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  fontWeight: location.pathname === route ? 700 : 600,
                  fontSize: 15,
                  letterSpacing: 0.2,
                  sx: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Bottom User Info - Fixed at Bottom */}
      <Box
        sx={{
          mt: "auto",
          px: 2,
          py: 2,
          borderTop: "1px solid #ededed",
          display: { xs: "none", sm: "none", md: "flex" },
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          background: "#fff",
          flexShrink: 0,
          minHeight: 70,
        }}
        onClick={() =>
          navigate("/seller-dashboard/settings", { replace: true })
        }
      >
        <Avatar
          alt={user?.name}
          src={user?.profileImage?.url || "https://via.placeholder.com/60"}
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#0b4064",
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            flexShrink: 0,
            "& img": {
              borderRadius: "50%",
            },
          }}
        />

        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#2e4254",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: "#7a869a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user?.businessName}
          </Typography>
        </Box>

        <IconButton size="small">
          <Settings sx={{ fontSize: 20, color: "#7a869a" }} />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <>
      <PageTitle
        title="Seller Dashboard - BizBridge"
        description="Manage your products and business on BizBridge"
        keywords="seller dashboard, product management, business management"
        url="/seller-dashboard"
      />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          width: "100%",
          minWidth: 0,
          overflowX: "hidden",
          background: "#f2f3f7",
          boxSizing: "border-box",
        }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              background: "#fff",
              borderRight: "1px solid #ededed",
              overflow: "hidden",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {drawerContents}
        </Drawer>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: "100vh",
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: 60,
              background: "#fff",
              borderBottom: "1px solid #ededed",
              display: "flex",
              alignItems: "center",
              px: { xs: 2, sm: 4 },
              position: "sticky",
              top: 0,
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(true)}
                sx={{ mr: 2 }}
                aria-label="open drawer"
                size="large"
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Profile Completion Badge in Header (Mobile) */}
            {isMobile && profileCompletion && (
              <Tooltip
                title={`Profile: ${profileCompletion.percentage}% ${profileCompletion.isProfileComplete ? "✓ Complete" : ""}`}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                    p: 0.5,
                    cursor: "pointer",
                    borderRadius: 1,
                    backgroundColor:
                      getCompletionColor(profileCompletion.percentage) + "15",
                  }}
                  onClick={() => navigate("/seller-dashboard/settings")}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: getCompletionColor(
                        profileCompletion.percentage,
                      ),
                      mr: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/seller-dashboard/settings")}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color={getCompletionColor(profileCompletion.percentage)}
                  >
                    {profileCompletion.percentage}%
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {user?.name && (
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  alignSelf: "center",
                  display: { xs: "none", sm: "none", md: "flex" },
                }}
              >
                {user.name}
              </Typography>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Profile Completion Indicator (Desktop) */}
            {!isMobile && profileCompletion && (
              <Tooltip
                title={`Profile Completion: ${profileCompletion.percentage}% - ${getCompletionStatus(profileCompletion.percentage)}. Click to complete.`}
              >
                <Button
                  onClick={() => navigate("/seller-dashboard/settings")}
                  sx={{
                    mr: 2,
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor:
                      getCompletionColor(profileCompletion.percentage) + "15",
                    color: getCompletionColor(profileCompletion.percentage),
                    border: `1px solid ${getCompletionColor(profileCompletion.percentage)}30`,
                    "&:hover": {
                      backgroundColor:
                        getCompletionColor(profileCompletion.percentage) + "25",
                    },
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: getCompletionColor(
                        profileCompletion.percentage,
                      ),
                    }}
                  />
                  Profile: {profileCompletion.percentage}%
                </Button>
              </Tooltip>
            )}

            {/* View As Dropdown */}
            {/* <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mr: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#6b7280", mr: 1 }}>
                View as:
              </Typography>

              <Button
                onClick={openViewMenu}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#0b4064",
                  fontSize: 14,
                  px: 1,
                }}
                endIcon={<span style={{ fontSize: 16 }}>▼</span>}
              >
                {isSeller ? "Seller" : "Buyer"}
              </Button>

              <Menu
                anchorEl={viewMenuAnchor}
                open={Boolean(viewMenuAnchor)}
                onClose={closeViewMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <MenuItem
                  selected={isSeller}
                  onClick={() => {
                    setIsSeller(true);
                    dispatch(updateUserMode({ id: user._id, mode: "seller" }));
                    navigate("/seller-dashboard", { replace: true });
                    closeViewMenu();
                  }}
                >
                  Seller
                </MenuItem>

                <MenuItem
                  selected={!isSeller}
                  onClick={() => {
                    setIsSeller(false);
                    dispatch(updateUserMode({ id: user._id, mode: "buyer" }));
                    navigate("/buyer-dashboard", { replace: true });
                    closeViewMenu();
                  }}
                >
                  Buyer
                </MenuItem>
              </Menu>
            </Box> */}

            <Avatar
              alt={user?.name}
              src={user?.profileImage?.url || "https://via.placeholder.com/60"}
              onClick={handleProfileIconClick}
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "#0b4064",
                color: "#fff",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                flexShrink: 0,
                cursor: "pointer",
                position: "relative",
                "& img": {
                  borderRadius: "50%",
                },
              }}
            />

            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem>
                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: "pointer",
                    // background: "#fff",
                    flexShrink: 0,
                  }}
                  onClick={() =>
                    navigate("/seller-dashboard/settings", { replace: true })
                  }
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      alt={user?.name}
                      src={
                        user?.profileImage?.url ||
                        "https://via.placeholder.com/60"
                      }
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        background: "#0b4064",
                        color: "#fff",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        flexShrink: 0,
                        "& img": {
                          borderRadius: "50%",
                        },
                      }}
                    />
                  </Box>

                  <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#2e4254",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user?.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#7a869a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {user?.businessName}
                    </Typography>
                  </Box>

                  <IconButton size="small">
                    <Settings sx={{ fontSize: 20, color: "#7a869a" }} />
                  </IconButton>
                </Box>
              </MenuItem>
            </Menu>
          </Box>

          {/* Content */}
          <Box
            sx={{
              p: { xs: 1, sm: 2, md: 4 },
              width: "100%",
              mx: "auto",
              minHeight: "calc(100vh - 60px)",
              boxSizing: "border-box",
              overflowY: "auto",
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
}
